import React from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, GiftIcon, GlobeIcon } from 'lucide-react';
import { SectionLabel } from './SectionLabel';
import { websiteTiers, domainTerms } from '../data/site';

/* 2026-09-19 (Kevin, website-lane launch): cards now show page count and
   turnaround under the price, plus a "Launch bonus" block per tier (the
   promo borrows from the tier above). Domain terms sit under the grid in
   plain view so they match the Stripe checkout terms. Heading no longer says
   "You own it either way", because Viridia holds the domain. */
export function Websites() {
  return <section id="tiers" className="border-b border-[#1a1a1a] bg-[#0c0c0c]" data-id="element-824" data-mp="1ng5h6b5dwff9">
      <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-28" data-id="element-825" data-mp="1igateggwo5yf">
        <SectionLabel data-id="element-826" data-mp="1ii3w5koee615">What you get</SectionLabel>

        <div className="mt-8 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between" data-id="element-827" data-mp="2yt7xr67hh4zf">
          <h2 className="max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-828" data-mp="2tvzcgwr1gkty">
            Three tiers, one time. Live in 2 to 3 days.
          </h2>
          <p className="max-w-sm text-sm leading-relaxed text-neutral-500" data-id="element-829" data-mp="1jdph1bl7qov3">
            $20 a month to keep it hosted, fast, and online, on every tier. Updates included. Your
            first month is on us.
          </p>
        </div>

        <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-2" data-id="element-830" data-mp="1sdcks41f06rb">
          <GiftIcon className="h-4 w-4 text-emerald-500" aria-hidden="true" data-id="element-831" data-mp="vmacrd9vlxh7" />
          <span className="text-sm font-medium text-emerald-400" data-id="element-832" data-mp="10nzpqygnsl7x">
            Launch promo: every tier comes with a piece of the tier above it.
          </span>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3" data-id="element-833" data-mp="19gtfjptqf301">
          {websiteTiers.map(t => <article key={t.name} className={`flex flex-col rounded-2xl border p-7 ${t.featured ? 'border-emerald-500/50 bg-[#0e1310]' : 'border-[#1c1c1c] bg-[#0d0d0d]'}`} data-id="element-834" data-mp="cx40a7zoe23u">
              <div className="flex items-center justify-between gap-3" data-id="element-835" data-mp="1fc3368mwr58c">
                <h3 className="font-mono text-xs uppercase tracking-[0.18em] text-neutral-400" data-id="element-836" data-mp="1tzy1c4joq1st">
                  {t.name}
                </h3>
                {t.featured && <span className="rounded-full bg-emerald-500/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-500" data-id="element-837" data-mp="1p3h0qavatuyu">
                    Most popular
                  </span>}
              </div>

              <p className="mt-6 flex items-baseline gap-2" data-id="element-838" data-mp="1acgr42itfv14">
                <span className="text-3xl font-bold tracking-tight text-white" data-id="element-839" data-mp="1a9vrlk5zpr6j">{t.price}</span>
                <span className="text-sm text-neutral-500" data-id="element-840" data-mp="16czkv6vketuw">one-time</span>
              </p>
              <p className="mt-2 text-sm text-neutral-300" data-id="element-841" data-mp="2ltgj0x1947um">{t.pages}</p>
              <p className="mt-1 font-mono text-xs uppercase tracking-[0.15em] text-emerald-500" data-id="element-842" data-mp="2hp8sqvf5ajua">
                {t.hosting}
              </p>

              <ul className="mt-7 space-y-3 border-t border-[#1c1c1c] pt-7" data-id="element-843" data-mp="2u8e82tfwsk1j">
                {t.includes.map(inc => <li key={inc} className="flex gap-2.5 text-sm leading-snug text-neutral-300" data-id="element-844" data-mp="2fbwwq27pz5ez">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" data-id="element-845" data-mp="2k9oql1rxd1lt" />
                    {inc}
                  </li>)}
              </ul>

              <div className="mt-7 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] p-4" data-id="element-846" data-mp="3d0yjsy3h88i4">
                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-500" data-id="element-847" data-mp="2y7wopd0p68yk">
                  <GiftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-848" data-mp="3qywy63hss4xm" />
                  Launch bonus
                </p>
                <ul className="mt-3 space-y-2.5" data-id="element-849" data-mp="3vpn0f7uxfro3">
                  {t.bonus.map(b => <li key={b} className="flex gap-2.5 text-sm leading-snug text-neutral-200" data-id="element-850" data-mp="281brzcx0e9cd">
                      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" data-id="element-851" data-mp="231fk32l6xxrj" />
                      {b}
                    </li>)}
                </ul>
              </div>

              <div className="flex-1" data-id="element-852" data-mp="1t4uh0msqewqc" />

              <Link to="/contact" className={`mt-8 rounded-full px-4 py-3 text-center text-sm font-semibold transition-colors ${t.featured ? 'bg-emerald-500 text-[#0a0a0a] hover:bg-emerald-400' : 'border border-[#2a2a2a] text-neutral-200 hover:border-emerald-500/60 hover:text-white'}`} data-id="element-853" data-mp="1t5vtmvpebra6">
                Get my free mock-up
              </Link>
            </article>)}
        </div>

        <div className="mt-8 flex gap-3 rounded-2xl border border-[#1c1c1c] bg-[#0d0d0d] p-6" data-id="element-854" data-mp="39jvmxntqkiog">
          <GlobeIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" aria-hidden="true" data-id="element-855" data-mp="34phofcjs1vfk" />
          <p className="text-sm leading-relaxed text-neutral-400" data-id="element-856" data-mp="3d2bbiwk3jiou">{domainTerms}</p>
        </div>
      </div>
    </section>;
}